import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import { ResponseErrorWithHandled } from '../../shared/types';
import {
  getEmailAccountsInWarmupRequest,
  getEmailAccountWarmupDeliverabilityReportRequest,
  getEmailAccountWarmupSentReceivedReportRequest,
  getEmailAccountWarmupSettingsRequest,
  saveEmailAccountWarmupSettingsRequest,
  turnOnOffEmailAccountWarmupRequest,
  getEmailAccountWarmupListRequest,
  importEmailAccountCsvRequest,
  syncSaleshandyEmailAccountWithApiKeyRequest,
  getEmailAccountsTagsRequest,
  emailAccountTagsAssignRequest,
  emailAccountTagsBulkAssignRequest,
  emailAccountTagsUnassignRequest,
  emailAccountTagsBulkUnassignRequest,
  saveBulkEmailAccountWarmupSettingsRequest,
  updateAllEmailAccountWarmupSettingsRequest,
  enableDisableDeleteSelectedEmailAccountWarmupRequest,
  enableDisableDeleteBulkEmailAccountWarmupRequest,
  getEmailAccountsBulkActionStatusRequest,
  refreshEmailAccountRequest,
  saveEmailWarmupErrorMessageRequest,
  getEmailAccountWarmupReportStatsRequest,
  getEmailAccountWarmupReportDeliverabilityScoreRequest,
  getEmailAccountWarmupReportESPBreakdownRequest,
  getEmailAccountWarmupReportGraphRequest,
  getEmailAccountWarmupReportExportRequest,
  emailAccountsWarmupExportRequest,
  sendTableEmailWarmupSettingsStrategyEditRequest,
  getEmailAccountWarmupBulkRunningStoppedStatusRequest,
  getEmailActivitySummaryRequest,
  getEmailActivityEventsRequest,
  getWarmupActivityHistoryRequest,
} from './extra-actions';
import {
  EmailAccountTag,
  EmailAccountFilters,
  EmailAccountWarmupDeliverabilityReport,
  EmailAccountWarmupFilterDropdowns,
  EmailAccountWarmupFilters,
  EmailAccountWarmupList,
  EmailAccountWarmupSentReceivedReport,
  EmailAccountWarmupSettings,
  EmailWarmupAccount,
  EmailAccountWarmupFilterRevamp,
  EmailActivitySummary,
  EmailActivityCycle,
  WarmupActivityEvent,
} from './types';
import { getEmailAccountsFiltersFromLocalStore } from './helpers/email-filters-resolver';
import { defaultFilterRows } from './components/advance-filter/standalone-filter-example';
import { ReportsBy } from './components/email-warmup-report/email-warmup-report-types';

type RequestState = {
  status: RequestStatus;
  message: string;
  error: ResponseErrorWithHandled;
};

export const emailAccountFiltersInitialState: EmailAccountWarmupFilters = {
  tags: [],
  status: [],
  warmupStartDateFrom: null,
  warmupStartDateTo: null,
};

export const warmupEmailAccountInitialState: EmailAccountFilters = {
  tags: [],
  status: [],
  'created date': [],
};

export const warmupEmailAccountFilterRevampInitialState: EmailAccountWarmupFilterRevamp = {
  outreachReadiness: [],
  warmupStatus: [],
  volumeMode: undefined,
  espVolumeFilter: defaultFilterRows,
  strategyTypes: [],
  domain: '',
  warmupStartDate: [],
  setupScore: [0, 100],
  deliverabilityScore: [0, 100],
  tags: [],
  search: '',
};

type State = {
  getEmailAccountsInWarmupRequest: RequestState;
  getEmailAccountWarmupDeliverabilityReportRequest: RequestState;
  getEmailAccountWarmupSentReceivedReportRequest: RequestState;
  getEmailAccountWarmupSettingsRequest: RequestState;
  getEmailAccountWarmupBulkRunningStoppedStatusRequest: RequestState;
  saveEmailAccountWarmupSettingsRequest: RequestState;
  turnOnOffEmailAccountWarmupRequest: RequestState;
  getEmailAccountWarmupListRequest: RequestState;
  importEmailAccountCsvRequest: RequestState;
  syncSaleshandyEmailAccountWithApiKeyRequest: RequestState;
  getEmailAccountsTagsRequest: RequestState;
  emailAccountTagsAssignRequest: RequestState;
  emailAccountTagsBulkAssignRequest: RequestState;
  emailAccountTagsUnassignRequest: RequestState;
  emailAccountTagsBulkUnassignRequest: RequestState;
  saveBulkEmailAccountWarmupSettingsRequest: RequestState;
  updateAllEmailAccountWarmupSettingsRequest: RequestState;
  enableDisableDeleteSelectedEmailAccountWarmupRequest: RequestState;
  enableDisableDeleteBulkEmailAccountWarmupRequest: RequestState;
  getEmailAccountsBulkActionStatusRequest: RequestState;
  emailWarmupAccounts: EmailWarmupAccount[];
  warmupBulkActionStatus: {
    'warmup-bulk-delete': boolean;
    'warmup-bulk-disable': boolean;
    'warmup-bulk-enable': boolean;
  };
  emailAccountWarmupSentReceivedReport: EmailAccountWarmupSentReceivedReport;
  emailAccountWarmupDeliverabilityReport: EmailAccountWarmupDeliverabilityReport;
  emailAccountWarmupSettings: EmailAccountWarmupSettings;
  emailAccountWarmupList: EmailAccountWarmupList[];
  totalEmailWarmupAccounts: number;
  selectedEmailWarmupAccounts: EmailWarmupAccount[];
  emailAccountTagsList: EmailAccountTag[];

  selectedEmails: any[];
  allSelectedEmailsDetails: {
    isAllProspectsSelected?: boolean;
    deSelectedContactIds?: any[];
  };
  emailAccountsFilters: EmailAccountWarmupFilters;
  warmupEmailAccountFilter: EmailAccountWarmupFilterDropdowns;

  warmupEmailAccountFilterRevamp: EmailAccountWarmupFilterRevamp;
  filtersWereReset: boolean;
  refreshEmailAccountRequest: RequestState;
  saveEmailWarmupErrorMessageRequest: RequestState;
  saveEmailWarmupErrorMessageResponseErrorMessage: string | null;
  saveEmailWarmupErrorMessageResponseSolution: string | null;
  refreshingEmailAccountId: number | null;

  emailReportsGraphData: null;
  emailReportsReportsBy: ReportsBy.ByEmailWarmupReport;
  emailReportsSummary: null;
  reportsBy: ReportsBy.ByEmailWarmupReport;
  getEmailGraphDataRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };
  getEmailReportsSummaryRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };
  getSelectedEmailsRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };
  getSelectedSequencesRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };
  getReportsSummaryRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };
  getSequenceReportsSummaryRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };

  getGraphDataRequest: {
    status: RequestStatus.Ideal;
    message: null;
    error: null;
  };
  reportsSummary: null;
  emailAccountWarmupReportStats: null;
  getEmailAccountWarmupReportStatsRequest: RequestState;
  emailAccountWarmupReportDeliverabilityScore: null;
  getEmailAccountWarmupReportDeliverabilityScoreRequest: RequestState;
  emailAccountWarmupReportESPBreakdown: null;
  getEmailAccountWarmupReportESPBreakdownRequest: RequestState;
  getEmailAccountWarmupReportGraphRequest: RequestState;
  emailAccountWarmupReportExport: null;
  getEmailAccountWarmupReportExportRequest: RequestState;
  emailAccountsWarmupExportRequest: RequestState;
  sendTableEmailWarmupSettingsStrategyEditRequest: RequestState;
  getEmailAccountWarmupBulkRunningStoppedStatusPayload: {
    running: number;
    stopped: number;
  } | null;
  emailActivitySummary: EmailActivitySummary | null;
  emailActivityEventsByDay: Record<number, EmailActivityCycle[]>;
  emailActivityEventsLoadingByDay: Record<number, boolean>;
  emailActivityEventsHasMoreByDay: Record<number, boolean>;
  emailActivityEventsLastEventAtByDay: Record<number, string | null>;
  warmupActivityHistory: WarmupActivityEvent[];
  getEmailActivitySummaryRequest: RequestState;
  getEmailActivityEventsRequest: RequestState;
  getWarmupActivityHistoryRequest: RequestState;
};

const initialState: State = {
  getEmailAccountsInWarmupRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupDeliverabilityReportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupSentReceivedReportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupBulkRunningStoppedStatusRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveEmailAccountWarmupSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  turnOnOffEmailAccountWarmupRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  importEmailAccountCsvRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  syncSaleshandyEmailAccountWithApiKeyRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountsTagsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountTagsAssignRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountTagsBulkAssignRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountTagsUnassignRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountTagsBulkUnassignRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveBulkEmailAccountWarmupSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateAllEmailAccountWarmupSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  enableDisableDeleteSelectedEmailAccountWarmupRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  enableDisableDeleteBulkEmailAccountWarmupRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountsBulkActionStatusRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  refreshEmailAccountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveEmailWarmupErrorMessageRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  saveEmailWarmupErrorMessageResponseErrorMessage: null,
  saveEmailWarmupErrorMessageResponseSolution: null,
  refreshingEmailAccountId: null,
  emailWarmupAccounts: [],
  warmupBulkActionStatus: {
    'warmup-bulk-delete': false,
    'warmup-bulk-disable': false,
    'warmup-bulk-enable': false,
  },
  totalEmailWarmupAccounts: 0,
  emailAccountWarmupSentReceivedReport: null,
  emailAccountWarmupDeliverabilityReport: null,
  emailAccountWarmupSettings: null,
  emailAccountWarmupList: null,
  selectedEmailWarmupAccounts: [],
  emailAccountTagsList: [],
  selectedEmails: [],
  allSelectedEmailsDetails: null,
  emailAccountsFilters:
    getEmailAccountsFiltersFromLocalStore() || emailAccountFiltersInitialState,
  warmupEmailAccountFilter: warmupEmailAccountInitialState,
  warmupEmailAccountFilterRevamp: warmupEmailAccountFilterRevampInitialState,
  filtersWereReset: false,

  emailReportsGraphData: null,
  emailReportsReportsBy: ReportsBy.ByEmailWarmupReport,
  emailReportsSummary: null,
  reportsBy: ReportsBy.ByEmailWarmupReport,
  getEmailGraphDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailReportsSummaryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSelectedEmailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSelectedSequencesRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getReportsSummaryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSequenceReportsSummaryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getGraphDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  reportsSummary: null,
  emailAccountWarmupReportStats: null,
  getEmailAccountWarmupReportStatsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountWarmupReportDeliverabilityScore: null,
  getEmailAccountWarmupReportDeliverabilityScoreRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountWarmupReportESPBreakdown: null,
  getEmailAccountWarmupReportESPBreakdownRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupReportGraphRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  getEmailAccountWarmupReportExportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailAccountWarmupReportExport: null,
  emailAccountsWarmupExportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  sendTableEmailWarmupSettingsStrategyEditRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailAccountWarmupBulkRunningStoppedStatusPayload: {
    running: 0,
    stopped: 0,
  },
  emailActivitySummary: null,
  emailActivityEventsByDay: {},
  emailActivityEventsLoadingByDay: {},
  emailActivityEventsHasMoreByDay: {},
  emailActivityEventsLastEventAtByDay: {},
  warmupActivityHistory: [],
  getEmailActivitySummaryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailActivityEventsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getWarmupActivityHistoryRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
};

const emailWarmupSlice = createSlice({
  name: 'emailWarmup',
  initialState,
  reducers: {
    resetEmailWarmupState: (state) => {
      state.getEmailAccountsInWarmupRequest =
        initialState.getEmailAccountsInWarmupRequest;
      state.getEmailAccountWarmupDeliverabilityReportRequest =
        initialState.getEmailAccountWarmupDeliverabilityReportRequest;
      state.getEmailAccountWarmupSentReceivedReportRequest =
        initialState.getEmailAccountWarmupSentReceivedReportRequest;
      state.getEmailAccountWarmupSettingsRequest =
        initialState.getEmailAccountWarmupSettingsRequest;
      state.getEmailAccountWarmupBulkRunningStoppedStatusRequest =
        initialState.getEmailAccountWarmupBulkRunningStoppedStatusRequest;
      state.saveEmailAccountWarmupSettingsRequest =
        initialState.saveEmailAccountWarmupSettingsRequest;
      state.turnOnOffEmailAccountWarmupRequest =
        initialState.turnOnOffEmailAccountWarmupRequest;
      state.getEmailAccountWarmupListRequest =
        initialState.getEmailAccountWarmupListRequest;
      state.getEmailAccountsTagsRequest =
        initialState.getEmailAccountsTagsRequest;
      state.emailAccountTagsAssignRequest =
        initialState.emailAccountTagsAssignRequest;
      state.emailAccountTagsBulkAssignRequest =
        initialState.emailAccountTagsBulkAssignRequest;
      state.emailAccountTagsUnassignRequest =
        initialState.emailAccountTagsUnassignRequest;
      state.emailAccountTagsBulkUnassignRequest =
        initialState.emailAccountTagsBulkUnassignRequest;
      state.emailWarmupAccounts = initialState.emailWarmupAccounts;
      state.emailAccountWarmupSentReceivedReport =
        initialState.emailAccountWarmupSentReceivedReport;
      state.emailAccountWarmupDeliverabilityReport =
        initialState.emailAccountWarmupDeliverabilityReport;
      state.emailAccountWarmupSettings =
        initialState.emailAccountWarmupSettings;
      state.emailAccountWarmupList = initialState.emailAccountWarmupList;
      state.totalEmailWarmupAccounts = initialState.totalEmailWarmupAccounts;
      state.selectedEmailWarmupAccounts =
        initialState.selectedEmailWarmupAccounts;
      state.emailAccountTagsList = initialState.emailAccountTagsList;
      state.getEmailAccountWarmupReportStatsRequest =
        initialState.getEmailAccountWarmupReportStatsRequest;
      state.getEmailAccountWarmupReportDeliverabilityScoreRequest =
        initialState.getEmailAccountWarmupReportDeliverabilityScoreRequest;
      state.getEmailAccountWarmupReportESPBreakdownRequest =
        initialState.getEmailAccountWarmupReportESPBreakdownRequest;
      state.getEmailAccountWarmupReportGraphRequest =
        initialState.getEmailAccountWarmupReportGraphRequest;
      state.getEmailAccountWarmupReportExportRequest =
        initialState.getEmailAccountWarmupReportExportRequest;
      state.emailAccountWarmupReportStats =
        initialState.emailAccountWarmupReportStats;
      state.emailAccountWarmupReportDeliverabilityScore =
        initialState.emailAccountWarmupReportDeliverabilityScore;
      state.emailAccountWarmupReportESPBreakdown =
        initialState.emailAccountWarmupReportESPBreakdown;
      state.reportsSummary = initialState.reportsSummary;
      state.emailActivitySummary = initialState.emailActivitySummary;
      state.emailActivityEventsByDay = initialState.emailActivityEventsByDay;
      state.emailActivityEventsLoadingByDay =
        initialState.emailActivityEventsLoadingByDay;
      state.emailActivityEventsHasMoreByDay =
        initialState.emailActivityEventsHasMoreByDay;
      state.emailActivityEventsLastEventAtByDay =
        initialState.emailActivityEventsLastEventAtByDay;
      state.warmupActivityHistory = initialState.warmupActivityHistory;
      state.getEmailActivitySummaryRequest =
        initialState.getEmailActivitySummaryRequest;
      state.getEmailActivityEventsRequest =
        initialState.getEmailActivityEventsRequest;
      state.getWarmupActivityHistoryRequest =
        initialState.getWarmupActivityHistoryRequest;
    },
    resetTurnOnOffEmailAccountWarmupState: (state) => {
      state.turnOnOffEmailAccountWarmupRequest =
        initialState.turnOnOffEmailAccountWarmupRequest;
    },
    resetSaveEmailAccountWarmupSettingsRequestState: (state) => {
      state.saveEmailAccountWarmupSettingsRequest =
        initialState.saveEmailAccountWarmupSettingsRequest;
    },
    resetSaveBulkEmailAccountWarmupSettingsRequestState: (state) => {
      state.saveBulkEmailAccountWarmupSettingsRequest =
        initialState.saveBulkEmailAccountWarmupSettingsRequest;
    },
    resetUpdateAllEmailAccountWarmupSettingsRequestState: (state) => {
      state.updateAllEmailAccountWarmupSettingsRequest =
        initialState.updateAllEmailAccountWarmupSettingsRequest;
    },
    checkSingle: (state, action) => {
      if (action.payload.status === true) {
        if (state.allSelectedEmailsDetails?.isAllProspectsSelected) {
          state.allSelectedEmailsDetails.deSelectedContactIds = state.allSelectedEmailsDetails.deSelectedContactIds.filter(
            (id) => id !== action.payload.row.id,
          );
        }
        state.selectedEmails = state.selectedEmails.concat([
          action.payload.row,
        ]);
      } else {
        if (state.allSelectedEmailsDetails?.isAllProspectsSelected) {
          state.allSelectedEmailsDetails.deSelectedContactIds = state.allSelectedEmailsDetails?.deSelectedContactIds.concat(
            [action.payload.row.id],
          );
        }
        state.selectedEmails = state.selectedEmails.filter(
          ({ id }) => id !== action.payload.row.id,
        );
      }
    },
    checkAll: (state, action) => {
      if (action.payload.status === true) {
        if (state.allSelectedEmailsDetails?.isAllProspectsSelected) {
          action.payload.rows.forEach(({ id: contactId }) => {
            state.allSelectedEmailsDetails.deSelectedContactIds = state.allSelectedEmailsDetails.deSelectedContactIds.filter(
              (id) => contactId !== id,
            );
          });
        }
        state.selectedEmails = state.selectedEmails.concat(action.payload.rows);
      } else {
        if (state.allSelectedEmailsDetails?.isAllProspectsSelected) {
          action.payload.rows.forEach(({ id }) => {
            state.allSelectedEmailsDetails.deSelectedContactIds = state.allSelectedEmailsDetails?.deSelectedContactIds.concat(
              [id],
            );
          });
        }
        state.selectedEmails = state.selectedEmails.reduce((acc, item) => {
          let isPresent = false;
          action.payload.rows.forEach(({ id }) => {
            if (id === item.id) {
              isPresent = true;
            }
          });
          if (isPresent) {
            return acc;
          }
          return [...acc, item];
        }, []);
      }
    },
    clearSelection: (state) => {
      state.allSelectedEmailsDetails = initialState.allSelectedEmailsDetails;
      state.selectedEmails = initialState.selectedEmails;
      state.emailAccountTagsAssignRequest =
        initialState.emailAccountTagsAssignRequest;
      state.emailAccountTagsBulkAssignRequest =
        initialState.emailAccountTagsBulkAssignRequest;
      state.emailAccountTagsUnassignRequest =
        initialState.emailAccountTagsUnassignRequest;
      state.emailAccountTagsBulkUnassignRequest =
        initialState.emailAccountTagsBulkUnassignRequest;
      state.saveBulkEmailAccountWarmupSettingsRequest =
        initialState.saveBulkEmailAccountWarmupSettingsRequest;
      state.updateAllEmailAccountWarmupSettingsRequest =
        initialState.updateAllEmailAccountWarmupSettingsRequest;
      state.enableDisableDeleteSelectedEmailAccountWarmupRequest =
        initialState.enableDisableDeleteSelectedEmailAccountWarmupRequest;
      state.sendTableEmailWarmupSettingsStrategyEditRequest =
        initialState.sendTableEmailWarmupSettingsStrategyEditRequest;
      state.selectedEmails = initialState.selectedEmails;
      state.allSelectedEmailsDetails = initialState.allSelectedEmailsDetails;
    },
    resetWarmupListFilters: (state) => {
      state.emailAccountsFilters =
        getEmailAccountsFiltersFromLocalStore() ||
        emailAccountFiltersInitialState;
      state.warmupEmailAccountFilter = warmupEmailAccountInitialState;
      state.warmupEmailAccountFilterRevamp = warmupEmailAccountFilterRevampInitialState;
      state.selectedEmails = [];
      state.allSelectedEmailsDetails = null;
      state.filtersWereReset = true;
    },
    clearFiltersWereReset: (state) => {
      state.filtersWereReset = false;
    },
    selectAllContacts: (state, action) => {
      state.allSelectedEmailsDetails = action.payload;
    },
    setEmailAccountsFilters: (state, action) => {
      state.emailAccountsFilters = { ...action.payload };
    },
    setWarmupEmailAccountFilters: (state, action) => {
      state.warmupEmailAccountFilter = { ...action.payload };
    },
    setWarmupEmailAccountFiltersRevamp: (state, action) => {
      state.warmupEmailAccountFilterRevamp = { ...action.payload };
    },
    resetEmailAccountBulkActionStatusRequestState: (state) => {
      state.getEmailAccountsBulkActionStatusRequest =
        initialState.getEmailAccountsBulkActionStatusRequest;
    },
    resetRefreshEmailAccountRequestState: (state) => {
      state.refreshEmailAccountRequest =
        initialState.refreshEmailAccountRequest;
      state.refreshingEmailAccountId = null;
    },
    resetWarmupEmailAccountFilterRevamp: (state) => {
      state.warmupEmailAccountFilterRevamp =
        initialState.warmupEmailAccountFilterRevamp;
    },
    resetEmailAccountsWarmupExportRequestState: (state) => {
      state.emailAccountsWarmupExportRequest =
        initialState.emailAccountsWarmupExportRequest;
    },
  },
  extraReducers: (builder) => {
    // Get Email Account List In Warmup
    builder.addCase(getEmailAccountsInWarmupRequest.pending, (state) => {
      state.getEmailAccountsInWarmupRequest.status = RequestStatus.Pending;
      state.getEmailAccountsInWarmupRequest.error = null;
      state.getEmailAccountsInWarmupRequest.message = null;
    });
    builder.addCase(
      getEmailAccountsInWarmupRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountsInWarmupRequest.status = RequestStatus.Succeeded;
        state.emailWarmupAccounts = action.payload.payload.emailAccounts;
        state.totalEmailWarmupAccounts =
          action.payload.payload.emailAccountCount;
      },
    );
    builder.addCase(
      getEmailAccountsInWarmupRequest.rejected,
      (state, action) => {
        state.getEmailAccountsInWarmupRequest.status = RequestStatus.Failed;
        state.getEmailAccountsInWarmupRequest.message = action.payload.message;
        state.getEmailAccountsInWarmupRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Email Account Warmup Deliverability Report
    builder.addCase(
      getEmailAccountWarmupDeliverabilityReportRequest.pending,
      (state) => {
        state.getEmailAccountWarmupDeliverabilityReportRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupDeliverabilityReportRequest.error = null;
        state.getEmailAccountWarmupDeliverabilityReportRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupDeliverabilityReportRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupDeliverabilityReportRequest.status =
          RequestStatus.Succeeded;
        state.emailAccountWarmupDeliverabilityReport = action.payload.payload;
      },
    );
    builder.addCase(
      getEmailAccountWarmupDeliverabilityReportRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupDeliverabilityReportRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupDeliverabilityReportRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupDeliverabilityReportRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Email Account Warmup Sent Received Report
    builder.addCase(
      getEmailAccountWarmupSentReceivedReportRequest.pending,
      (state) => {
        state.getEmailAccountWarmupSentReceivedReportRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupSentReceivedReportRequest.error = null;
        state.getEmailAccountWarmupSentReceivedReportRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupSentReceivedReportRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupSentReceivedReportRequest.status =
          RequestStatus.Succeeded;
        state.emailAccountWarmupSentReceivedReport = action.payload.payload;
      },
    );
    builder.addCase(
      getEmailAccountWarmupSentReceivedReportRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupSentReceivedReportRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupSentReceivedReportRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupSentReceivedReportRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Email Account Warmup Settings
    builder.addCase(getEmailAccountWarmupSettingsRequest.pending, (state) => {
      state.getEmailAccountWarmupSettingsRequest.status = RequestStatus.Pending;
      state.getEmailAccountWarmupSettingsRequest.error = null;
      state.getEmailAccountWarmupSettingsRequest.message = null;
    });
    builder.addCase(
      getEmailAccountWarmupSettingsRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Succeeded;
        state.emailAccountWarmupSettings = action.payload.payload;
      },
    );
    builder.addCase(
      getEmailAccountWarmupSettingsRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupSettingsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Save Email Account Warmup Settings
    builder.addCase(saveEmailAccountWarmupSettingsRequest.pending, (state) => {
      state.saveEmailAccountWarmupSettingsRequest.status =
        RequestStatus.Pending;
      state.saveEmailAccountWarmupSettingsRequest.error = null;
      state.saveEmailAccountWarmupSettingsRequest.message = null;
    });
    builder.addCase(
      saveEmailAccountWarmupSettingsRequest.fulfilled,
      (state, action) => {
        state.saveEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Succeeded;
        state.saveEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      saveEmailAccountWarmupSettingsRequest.rejected,
      (state, action) => {
        state.saveEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Failed;
        state.saveEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
        state.saveEmailAccountWarmupSettingsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Save Bulk Email Account Warmup Settings
    builder.addCase(
      saveBulkEmailAccountWarmupSettingsRequest.pending,
      (state) => {
        state.saveBulkEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Pending;
        state.saveBulkEmailAccountWarmupSettingsRequest.error = null;
        state.saveBulkEmailAccountWarmupSettingsRequest.message = null;
      },
    );
    builder.addCase(
      saveBulkEmailAccountWarmupSettingsRequest.fulfilled,
      (state, action) => {
        state.saveBulkEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Succeeded;
        state.saveBulkEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      saveBulkEmailAccountWarmupSettingsRequest.rejected,
      (state, action) => {
        state.saveBulkEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Failed;
        state.saveBulkEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
        state.saveBulkEmailAccountWarmupSettingsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Save Bulk All Email Account Warmup Settings
    builder.addCase(
      updateAllEmailAccountWarmupSettingsRequest.pending,
      (state) => {
        state.updateAllEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Pending;
        state.updateAllEmailAccountWarmupSettingsRequest.error = null;
        state.updateAllEmailAccountWarmupSettingsRequest.message = null;
      },
    );
    builder.addCase(
      updateAllEmailAccountWarmupSettingsRequest.fulfilled,
      (state, action) => {
        state.updateAllEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Succeeded;
        state.updateAllEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      updateAllEmailAccountWarmupSettingsRequest.rejected,
      (state, action) => {
        state.updateAllEmailAccountWarmupSettingsRequest.status =
          RequestStatus.Failed;
        state.updateAllEmailAccountWarmupSettingsRequest.message =
          action.payload.message;
        state.updateAllEmailAccountWarmupSettingsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Turn On/Off Email Account Warmup Settings
    builder.addCase(turnOnOffEmailAccountWarmupRequest.pending, (state) => {
      state.turnOnOffEmailAccountWarmupRequest.status = RequestStatus.Pending;
      state.turnOnOffEmailAccountWarmupRequest.error = null;
      state.turnOnOffEmailAccountWarmupRequest.message = null;
    });
    builder.addCase(
      turnOnOffEmailAccountWarmupRequest.fulfilled,
      (state, action) => {
        state.turnOnOffEmailAccountWarmupRequest.status =
          RequestStatus.Succeeded;
        state.turnOnOffEmailAccountWarmupRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      turnOnOffEmailAccountWarmupRequest.rejected,
      (state, action) => {
        state.turnOnOffEmailAccountWarmupRequest.status = RequestStatus.Failed;
        state.turnOnOffEmailAccountWarmupRequest.message =
          action.payload.message;
        state.turnOnOffEmailAccountWarmupRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Email Account Warmup List
    builder.addCase(getEmailAccountWarmupListRequest.pending, (state) => {
      state.getEmailAccountWarmupListRequest.status = RequestStatus.Pending;
      state.getEmailAccountWarmupListRequest.error = null;
      state.getEmailAccountWarmupListRequest.message = null;
    });
    builder.addCase(
      getEmailAccountWarmupListRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupListRequest.status = RequestStatus.Succeeded;
        state.emailAccountWarmupList = action.payload.payload;
      },
    );
    builder.addCase(
      getEmailAccountWarmupListRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupListRequest.status = RequestStatus.Failed;
        state.getEmailAccountWarmupListRequest.message = action.payload.message;
        state.getEmailAccountWarmupListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Import Email Accounts Via CSV
    builder.addCase(importEmailAccountCsvRequest.pending, (state) => {
      state.importEmailAccountCsvRequest.status = RequestStatus.Pending;
      state.importEmailAccountCsvRequest.message = null;
      state.importEmailAccountCsvRequest.error = null;
    });
    builder.addCase(importEmailAccountCsvRequest.fulfilled, (state, action) => {
      state.importEmailAccountCsvRequest.status = RequestStatus.Succeeded;
      state.importEmailAccountCsvRequest.message = action.payload.message;
      state.importEmailAccountCsvRequest.error = null;
    });
    builder.addCase(importEmailAccountCsvRequest.rejected, (state, action) => {
      state.importEmailAccountCsvRequest.status = RequestStatus.Failed;
      state.importEmailAccountCsvRequest.message = action.payload.message;
      state.importEmailAccountCsvRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Saleshandy email account sync
    builder.addCase(
      syncSaleshandyEmailAccountWithApiKeyRequest.pending,
      (state) => {
        state.syncSaleshandyEmailAccountWithApiKeyRequest.status =
          RequestStatus.Pending;
        state.syncSaleshandyEmailAccountWithApiKeyRequest.message = null;
        state.syncSaleshandyEmailAccountWithApiKeyRequest.error = null;
      },
    );
    builder.addCase(
      syncSaleshandyEmailAccountWithApiKeyRequest.fulfilled,
      (state, action) => {
        state.syncSaleshandyEmailAccountWithApiKeyRequest.status =
          RequestStatus.Succeeded;
        state.syncSaleshandyEmailAccountWithApiKeyRequest.message =
          action.payload.message;
        state.syncSaleshandyEmailAccountWithApiKeyRequest.error = null;
      },
    );
    builder.addCase(
      syncSaleshandyEmailAccountWithApiKeyRequest.rejected,
      (state, action) => {
        state.syncSaleshandyEmailAccountWithApiKeyRequest.status =
          RequestStatus.Failed;
        state.syncSaleshandyEmailAccountWithApiKeyRequest.message =
          action.payload.message;
        state.syncSaleshandyEmailAccountWithApiKeyRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // Get Email Account Tags In Warmup Email Account Listing
    builder.addCase(getEmailAccountsTagsRequest.pending, (state) => {
      state.getEmailAccountsTagsRequest.status = RequestStatus.Pending;
      state.getEmailAccountsTagsRequest.error = null;
      state.getEmailAccountsTagsRequest.message = null;
    });
    builder.addCase(getEmailAccountsTagsRequest.fulfilled, (state, action) => {
      state.getEmailAccountsTagsRequest.status = RequestStatus.Succeeded;
      state.emailAccountTagsList = action.payload.payload;
    });
    builder.addCase(getEmailAccountsTagsRequest.rejected, (state, action) => {
      state.getEmailAccountsTagsRequest.status = RequestStatus.Failed;
      state.getEmailAccountsTagsRequest.message = action.payload.message;
      state.getEmailAccountsTagsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Assign Tags to Selected Email Accounts
    builder.addCase(emailAccountTagsAssignRequest.pending, (state) => {
      state.emailAccountTagsAssignRequest.status = RequestStatus.Pending;
      state.emailAccountTagsAssignRequest.error = null;
      state.emailAccountTagsAssignRequest.message = null;
    });
    builder.addCase(
      emailAccountTagsAssignRequest.fulfilled,
      (state, action) => {
        state.emailAccountTagsAssignRequest.status = RequestStatus.Succeeded;
        state.emailAccountTagsAssignRequest.message = action.payload.message;
      },
    );
    builder.addCase(emailAccountTagsAssignRequest.rejected, (state, action) => {
      state.emailAccountTagsAssignRequest.status = RequestStatus.Failed;
      state.emailAccountTagsAssignRequest.message = action.payload.message;
      state.emailAccountTagsAssignRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Assign Tags to Bulk Email Accounts
    builder.addCase(emailAccountTagsBulkAssignRequest.pending, (state) => {
      state.emailAccountTagsBulkAssignRequest.status = RequestStatus.Pending;
      state.emailAccountTagsBulkAssignRequest.error = null;
      state.emailAccountTagsBulkAssignRequest.message = null;
    });
    builder.addCase(
      emailAccountTagsBulkAssignRequest.fulfilled,
      (state, action) => {
        state.emailAccountTagsBulkAssignRequest.status =
          RequestStatus.Succeeded;
        state.emailAccountTagsBulkAssignRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      emailAccountTagsBulkAssignRequest.rejected,
      (state, action) => {
        state.emailAccountTagsBulkAssignRequest.status = RequestStatus.Failed;
        state.emailAccountTagsBulkAssignRequest.message =
          action.payload.message;
        state.emailAccountTagsBulkAssignRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Unassign Tags from Selected Email Accounts
    builder.addCase(emailAccountTagsUnassignRequest.pending, (state) => {
      state.emailAccountTagsUnassignRequest.status = RequestStatus.Pending;
      state.emailAccountTagsUnassignRequest.error = null;
      state.emailAccountTagsUnassignRequest.message = null;
    });
    builder.addCase(
      emailAccountTagsUnassignRequest.fulfilled,
      (state, action) => {
        state.emailAccountTagsUnassignRequest.status = RequestStatus.Succeeded;
        state.emailAccountTagsUnassignRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      emailAccountTagsUnassignRequest.rejected,
      (state, action) => {
        state.emailAccountTagsUnassignRequest.status = RequestStatus.Failed;
        state.emailAccountTagsUnassignRequest.message = action.payload.message;
        state.emailAccountTagsUnassignRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Unassign Tags from Bulk Email Accounts
    builder.addCase(emailAccountTagsBulkUnassignRequest.pending, (state) => {
      state.emailAccountTagsBulkUnassignRequest.status = RequestStatus.Pending;
      state.emailAccountTagsBulkUnassignRequest.error = null;
      state.emailAccountTagsBulkUnassignRequest.message = null;
    });
    builder.addCase(
      emailAccountTagsBulkUnassignRequest.fulfilled,
      (state, action) => {
        state.emailAccountTagsBulkUnassignRequest.status =
          RequestStatus.Succeeded;
        state.emailAccountTagsBulkUnassignRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      emailAccountTagsBulkUnassignRequest.rejected,
      (state, action) => {
        state.emailAccountTagsBulkUnassignRequest.status = RequestStatus.Failed;
        state.emailAccountTagsBulkUnassignRequest.message =
          action.payload.message;
        state.emailAccountTagsBulkUnassignRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Enable Disable Multi Selected Email Accounts
    builder.addCase(
      enableDisableDeleteSelectedEmailAccountWarmupRequest.pending,
      (state) => {
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.status =
          RequestStatus.Pending;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.error = null;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.message = null;
      },
    );
    builder.addCase(
      enableDisableDeleteSelectedEmailAccountWarmupRequest.fulfilled,
      (state, action) => {
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.status =
          RequestStatus.Succeeded;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      enableDisableDeleteSelectedEmailAccountWarmupRequest.rejected,
      (state, action) => {
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.status =
          RequestStatus.Failed;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.message =
          action.payload.message;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Enable Disable Delete Bulk Email Accounts
    builder.addCase(
      enableDisableDeleteBulkEmailAccountWarmupRequest.pending,
      (state) => {
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.status =
          RequestStatus.Pending;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.error = null;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.message = null;
      },
    );
    builder.addCase(
      enableDisableDeleteBulkEmailAccountWarmupRequest.fulfilled,
      (state, action) => {
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.status =
          RequestStatus.Succeeded;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      enableDisableDeleteBulkEmailAccountWarmupRequest.rejected,
      (state, action) => {
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.status =
          RequestStatus.Failed;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.message =
          action.payload.message;
        state.enableDisableDeleteSelectedEmailAccountWarmupRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Email Accounts Bulk Action Status
    builder.addCase(
      getEmailAccountsBulkActionStatusRequest.pending,
      (state) => {
        state.getEmailAccountsBulkActionStatusRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountsBulkActionStatusRequest.error = null;
        state.getEmailAccountsBulkActionStatusRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountsBulkActionStatusRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountsBulkActionStatusRequest.status =
          RequestStatus.Succeeded;
        state.warmupBulkActionStatus = action.payload.payload;
      },
    );
    builder.addCase(
      getEmailAccountsBulkActionStatusRequest.rejected,
      (state, action) => {
        state.getEmailAccountsBulkActionStatusRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountsBulkActionStatusRequest.message =
          action.payload.message;
        state.getEmailAccountsBulkActionStatusRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // refresh email account - email-warmup listing page
    builder.addCase(refreshEmailAccountRequest.pending, (state, action) => {
      state.refreshEmailAccountRequest.status = RequestStatus.Pending;
      state.refreshEmailAccountRequest.error = null;
      state.refreshEmailAccountRequest.message = null;
      state.refreshingEmailAccountId = action.meta.arg;
    });
    builder.addCase(refreshEmailAccountRequest.fulfilled, (state, action) => {
      state.refreshEmailAccountRequest.status = RequestStatus.Succeeded;
      state.refreshEmailAccountRequest.message = action.payload.message;
      state.refreshingEmailAccountId = null;

      // Update the specific email account in the list with the refreshed data
      const refreshedData = action?.payload?.payload;
      if (refreshedData && refreshedData?.id) {
        const index = state.emailWarmupAccounts.findIndex(
          (account) => account.id === refreshedData.id,
        );
        if (index !== -1) {
          state.emailWarmupAccounts[index] = {
            ...state.emailWarmupAccounts[index],
            ...refreshedData,
          };
        }
      }
    });
    builder.addCase(refreshEmailAccountRequest.rejected, (state, action) => {
      state.refreshEmailAccountRequest.status = RequestStatus.Failed;
      state.refreshEmailAccountRequest.message = action.payload.message;
      state.refreshEmailAccountRequest.error =
        !action.payload.isHandled && action.payload;
      state.refreshingEmailAccountId = null;
    });

    builder.addCase(saveEmailWarmupErrorMessageRequest.pending, (state) => {
      state.saveEmailWarmupErrorMessageRequest.status = RequestStatus.Pending;
      state.saveEmailWarmupErrorMessageRequest.error = null;
      state.saveEmailWarmupErrorMessageRequest.message = null;
      state.saveEmailWarmupErrorMessageResponseErrorMessage = null;
      state.saveEmailWarmupErrorMessageResponseSolution = null;
    });
    builder.addCase(
      saveEmailWarmupErrorMessageRequest.fulfilled,
      (state, action) => {
        state.saveEmailWarmupErrorMessageRequest.status =
          RequestStatus.Succeeded;
        state.saveEmailWarmupErrorMessageRequest.message =
          action.payload.message;
        const responsePayload = action.payload?.payload as
          | { errorMessage?: string; solution?: string | null }
          | undefined;
        const raw = responsePayload?.errorMessage ?? null;
        state.saveEmailWarmupErrorMessageResponseErrorMessage =
          typeof raw === 'string' ? raw : null;
        const sol = responsePayload?.solution ?? null;
        state.saveEmailWarmupErrorMessageResponseSolution =
          typeof sol === 'string' ? sol : null;
      },
    );
    builder.addCase(
      saveEmailWarmupErrorMessageRequest.rejected,
      (state, action) => {
        state.saveEmailWarmupErrorMessageRequest.status = RequestStatus.Failed;
        state.saveEmailWarmupErrorMessageRequest.message =
          action.payload?.message;
        state.saveEmailWarmupErrorMessageRequest.error =
          !action.payload?.isHandled && action.payload;
        state.saveEmailWarmupErrorMessageResponseErrorMessage = null;
        state.saveEmailWarmupErrorMessageResponseSolution = null;
      },
    );

    // report- Get Email Account Warmup Report Deliverability Score
    builder.addCase(
      getEmailAccountWarmupReportDeliverabilityScoreRequest.pending,
      (state) => {
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.error = null;
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportDeliverabilityScoreRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.status =
          RequestStatus.Succeeded;
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.message =
          action.payload.message;
        state.emailAccountWarmupReportDeliverabilityScore =
          action.payload.payload;
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.error = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportDeliverabilityScoreRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupReportDeliverabilityScoreRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // report- Get Email Account Warmup Report Stats
    builder.addCase(
      getEmailAccountWarmupReportStatsRequest.pending,
      (state) => {
        state.getEmailAccountWarmupReportStatsRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupReportStatsRequest.error = null;
        state.getEmailAccountWarmupReportStatsRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportStatsRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupReportStatsRequest.status =
          RequestStatus.Succeeded;
        state.getEmailAccountWarmupReportStatsRequest.message =
          action.payload.message;
        state.emailAccountWarmupReportStats = action.payload.payload;
        state.getEmailAccountWarmupReportStatsRequest.error = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportStatsRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupReportStatsRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupReportStatsRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupReportStatsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // report- Get Email Account Warmup Report ESP Breakdown
    builder.addCase(
      getEmailAccountWarmupReportESPBreakdownRequest.pending,
      (state) => {
        state.getEmailAccountWarmupReportESPBreakdownRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupReportESPBreakdownRequest.error = null;
        state.getEmailAccountWarmupReportESPBreakdownRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportESPBreakdownRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupReportESPBreakdownRequest.status =
          RequestStatus.Succeeded;
        state.getEmailAccountWarmupReportESPBreakdownRequest.message =
          action.payload.message;
        state.emailAccountWarmupReportESPBreakdown = action.payload.payload;
        state.getEmailAccountWarmupReportESPBreakdownRequest.error = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportESPBreakdownRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupReportESPBreakdownRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupReportESPBreakdownRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupReportESPBreakdownRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // report- Get Email Account Warmup Report Graph
    builder.addCase(
      getEmailAccountWarmupReportGraphRequest.pending,
      (state) => {
        state.getEmailAccountWarmupReportGraphRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupReportGraphRequest.error = null;
        state.getEmailAccountWarmupReportGraphRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportGraphRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupReportGraphRequest.status =
          RequestStatus.Succeeded;
        state.getEmailAccountWarmupReportGraphRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupReportGraphRequest.error = null;
        state.emailReportsGraphData =
          action.payload.payload?.data || action.payload.payload || null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportGraphRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupReportGraphRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupReportGraphRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupReportGraphRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
    // report- Get Email Account Warmup Report Export
    builder.addCase(
      getEmailAccountWarmupReportExportRequest.pending,
      (state) => {
        state.getEmailAccountWarmupReportExportRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupReportExportRequest.error = null;
        state.getEmailAccountWarmupReportExportRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportExportRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupReportExportRequest.status =
          RequestStatus.Succeeded;
        state.getEmailAccountWarmupReportExportRequest.message =
          action.payload.message;
        state.emailAccountWarmupReportExport = action.payload.payload;
        state.getEmailAccountWarmupReportExportRequest.error = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupReportExportRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupReportExportRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupReportExportRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupReportExportRequest.error =
          !action.payload.isHandled && action.payload;
        state.emailAccountWarmupReportExport = null;
      },
    );

    // Bulk warmup export (email-accounts-warmup-export)
    builder.addCase(emailAccountsWarmupExportRequest.pending, (state) => {
      state.emailAccountsWarmupExportRequest.status = RequestStatus.Pending;
      state.emailAccountsWarmupExportRequest.error = null;
      state.emailAccountsWarmupExportRequest.message = null;
    });
    builder.addCase(
      emailAccountsWarmupExportRequest.fulfilled,
      (state, action) => {
        state.emailAccountsWarmupExportRequest.status = RequestStatus.Succeeded;
        state.emailAccountsWarmupExportRequest.message = action.payload.message;
        state.emailAccountsWarmupExportRequest.error = null;
      },
    );
    builder.addCase(
      emailAccountsWarmupExportRequest.rejected,
      (state, action) => {
        state.emailAccountsWarmupExportRequest.status = RequestStatus.Failed;
        state.emailAccountsWarmupExportRequest.message =
          action.payload?.message ?? null;
        state.emailAccountsWarmupExportRequest.error =
          !action.payload?.isHandled && action.payload;
      },
    );

    // send table email warmup settings strategy edit
    builder.addCase(
      sendTableEmailWarmupSettingsStrategyEditRequest.pending,
      (state) => {
        state.sendTableEmailWarmupSettingsStrategyEditRequest.status =
          RequestStatus.Pending;
        state.sendTableEmailWarmupSettingsStrategyEditRequest.error = null;
        state.sendTableEmailWarmupSettingsStrategyEditRequest.message = null;
      },
    );
    builder.addCase(
      sendTableEmailWarmupSettingsStrategyEditRequest.fulfilled,
      (state, action) => {
        state.sendTableEmailWarmupSettingsStrategyEditRequest.status =
          RequestStatus.Succeeded;
        state.sendTableEmailWarmupSettingsStrategyEditRequest.message =
          action.payload.message;
        state.sendTableEmailWarmupSettingsStrategyEditRequest.error = null;

        // Update the account in list with new values (no table API refresh needed)
        const payload = action.meta.arg as {
          id: number;
          strategy?: number;
          warmUpInitialSendingLimit?: number;
          increaseEmailsByNumber?: number;
          maxSendingLimit?: number;
          replyRate?: number;
          warmupLanguage?: string;
          upcomingContentConfig?: { industry: string | null; topics: string[] };
        };
        const idx = state.emailWarmupAccounts.findIndex(
          (a) => Number(a.id) === Number(payload.id),
        );
        if (idx >= 0) {
          const acc = state.emailWarmupAccounts[idx] as any;
          if (acc) {
            if (payload.strategy !== undefined && acc.warmupConfig) {
              acc.warmupConfig = {
                ...acc.warmupConfig,
                strategy: payload.strategy,
              };
            }
            if (acc.warmupSettings) {
              acc.warmupSettings = { ...acc.warmupSettings };
              if (payload.warmUpInitialSendingLimit !== undefined) {
                acc.warmupSettings.warmUpInitialSendingLimit =
                  payload.warmUpInitialSendingLimit;
              }
              if (payload.increaseEmailsByNumber !== undefined) {
                acc.warmupSettings.increaseEmailsByNumber =
                  payload.increaseEmailsByNumber;
              }
              if (payload.maxSendingLimit !== undefined) {
                acc.warmupSettings.maxSendingLimit = payload.maxSendingLimit;
              }
              if (payload.replyRate !== undefined) {
                acc.warmupSettings.replyRate = payload.replyRate;
              }
              if (payload.warmupLanguage !== undefined) {
                acc.warmupSettings.warmupLanguage = payload.warmupLanguage;
              }
              if (payload.upcomingContentConfig !== undefined) {
                acc.warmupSettings.upcomingContentConfig =
                  payload.upcomingContentConfig;
              }
            }
          }
        }
      },
    );
    builder.addCase(
      sendTableEmailWarmupSettingsStrategyEditRequest.rejected,
      (state, action) => {
        state.sendTableEmailWarmupSettingsStrategyEditRequest.status =
          RequestStatus.Failed;
        state.sendTableEmailWarmupSettingsStrategyEditRequest.message =
          action.payload.message;
        state.sendTableEmailWarmupSettingsStrategyEditRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Email Account Warmup Bulk Running Stopped Status
    builder.addCase(
      getEmailAccountWarmupBulkRunningStoppedStatusRequest.pending,
      (state) => {
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.status =
          RequestStatus.Pending;
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.error = null;
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.message = null;
      },
    );
    builder.addCase(
      getEmailAccountWarmupBulkRunningStoppedStatusRequest.fulfilled,
      (state, action) => {
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.status =
          RequestStatus.Succeeded;
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.error = null;
        state.getEmailAccountWarmupBulkRunningStoppedStatusPayload =
          action.payload.payload;
      },
    );
    builder.addCase(
      getEmailAccountWarmupBulkRunningStoppedStatusRequest.rejected,
      (state, action) => {
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.status =
          RequestStatus.Failed;
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.message =
          action.payload.message;
        state.getEmailAccountWarmupBulkRunningStoppedStatusRequest.error =
          !action.payload.isHandled && action.payload;
        state.getEmailAccountWarmupBulkRunningStoppedStatusPayload = {
          running: 0,
          stopped: 0,
        };
      },
    );

    // activity- Get Email Activity Summary (email journey day rows)
    builder.addCase(getEmailActivitySummaryRequest.pending, (state) => {
      state.getEmailActivitySummaryRequest.status = RequestStatus.Pending;
      state.getEmailActivitySummaryRequest.error = null;
      state.getEmailActivitySummaryRequest.message = null;
    });
    builder.addCase(
      getEmailActivitySummaryRequest.fulfilled,
      (state, action) => {
        state.getEmailActivitySummaryRequest.status = RequestStatus.Succeeded;
        state.getEmailActivitySummaryRequest.message = action.payload.message;
        state.getEmailActivitySummaryRequest.error = null;
        state.emailActivitySummary = action.payload.payload;
      },
    );
    builder.addCase(
      getEmailActivitySummaryRequest.rejected,
      (state, action) => {
        state.getEmailActivitySummaryRequest.status = RequestStatus.Failed;
        state.getEmailActivitySummaryRequest.message = action.payload.message;
        state.getEmailActivitySummaryRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // activity- Get Email Activity Events
    builder.addCase(getEmailActivityEventsRequest.pending, (state, action) => {
      state.getEmailActivityEventsRequest.status = RequestStatus.Pending;
      state.getEmailActivityEventsRequest.error = null;
      state.getEmailActivityEventsRequest.message = null;
      state.emailActivityEventsLoadingByDay[action.meta.arg.dayOffset] = true;
    });
    builder.addCase(
      getEmailActivityEventsRequest.fulfilled,
      (state, action) => {
        state.getEmailActivityEventsRequest.status = RequestStatus.Succeeded;
        state.getEmailActivityEventsRequest.message = action.payload.message;
        state.getEmailActivityEventsRequest.error = null;
        const { dayOffset, lastEventAt: cursor } = action.meta.arg;
        const { cycles, hasMore, lastEventAt } = action.payload.payload ?? {};
        if (cursor) {
          state.emailActivityEventsByDay[dayOffset] = [
            ...(state.emailActivityEventsByDay[dayOffset] ?? []),
            ...(cycles ?? []),
          ];
        } else {
          state.emailActivityEventsByDay[dayOffset] = cycles ?? [];
        }
        state.emailActivityEventsHasMoreByDay[dayOffset] = hasMore ?? false;
        state.emailActivityEventsLastEventAtByDay[dayOffset] =
          lastEventAt ?? null;
        state.emailActivityEventsLoadingByDay[dayOffset] = false;
      },
    );
    builder.addCase(getEmailActivityEventsRequest.rejected, (state, action) => {
      state.getEmailActivityEventsRequest.status = RequestStatus.Failed;
      state.getEmailActivityEventsRequest.message = action.payload.message;
      state.getEmailActivityEventsRequest.error =
        !action.payload.isHandled && action.payload;
      state.emailActivityEventsLoadingByDay[action.meta.arg.dayOffset] = false;
    });

    // activity- Get Warmup Activity History (mailbox history)
    builder.addCase(getWarmupActivityHistoryRequest.pending, (state) => {
      state.getWarmupActivityHistoryRequest.status = RequestStatus.Pending;
      state.getWarmupActivityHistoryRequest.error = null;
      state.getWarmupActivityHistoryRequest.message = null;
    });
    builder.addCase(
      getWarmupActivityHistoryRequest.fulfilled,
      (state, action) => {
        state.getWarmupActivityHistoryRequest.status = RequestStatus.Succeeded;
        state.getWarmupActivityHistoryRequest.message = action.payload.message;
        state.getWarmupActivityHistoryRequest.error = null;
        state.warmupActivityHistory = action.payload.payload?.events ?? [];
      },
    );
    builder.addCase(
      getWarmupActivityHistoryRequest.rejected,
      (state, action) => {
        state.getWarmupActivityHistoryRequest.status = RequestStatus.Failed;
        state.getWarmupActivityHistoryRequest.message = action.payload.message;
        state.getWarmupActivityHistoryRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
  },
});

export const {
  resetEmailWarmupState,
  resetTurnOnOffEmailAccountWarmupState,
  checkSingle,
  checkAll,
  selectAllContacts,
  clearSelection,
  setEmailAccountsFilters,
  setWarmupEmailAccountFilters,
  setWarmupEmailAccountFiltersRevamp,
  resetWarmupListFilters,
  clearFiltersWereReset,
  resetEmailAccountBulkActionStatusRequestState,
  resetRefreshEmailAccountRequestState,
  resetWarmupEmailAccountFilterRevamp,
  resetSaveEmailAccountWarmupSettingsRequestState,
  resetSaveBulkEmailAccountWarmupSettingsRequestState,
  resetUpdateAllEmailAccountWarmupSettingsRequestState,
  resetEmailAccountsWarmupExportRequestState,
} = emailWarmupSlice.actions;

export default emailWarmupSlice.reducer;
